import React from 'react';
import { Select } from '@odin-labs/components';
import { to } from 'acl';
import { useGetWorkerAccessGroupSitesQuery } from 'apollo/generated/client-operations';
import { AccessGroupsError } from 'accessGroups/AccessGroupStatus';
import { LoadingError } from 'components/loadingError';
import { AuthContext } from 'auth/authContext';
import { WorkerTabProps } from 'containers/worker/types';
import { NewHeader } from 'components/header/NewHeader';
import { WorkerSiteAccessGroups } from 'accessGroups/WorkerSiteAccessGroups';
import { JobsiteFeatureFlagsProvider, useFlags } from 'featureFlags';

/**
 * The worker's access groups at a site, granted only where the site's
 * site-layout flag is on. With it off, memberships at a site whose zones mode
 * is not Off can still be revoked.
 */
function WorkerSiteAccessGroupsGrantingByFlag(
  props: Omit<React.ComponentProps<typeof WorkerSiteAccessGroups>, 'canGrant'>,
): React.ReactElement {
  const { siteLayoutUiEnabled } = useFlags();
  return <WorkerSiteAccessGroups {...props} canGrant={siteLayoutUiEnabled} />;
}

export function WorkerAccessGroups({
  worker,
  jobsiteWorkers = [],
  onTabApiChange,
}: WorkerTabProps): React.ReactElement {
  const { currentUser: user } = React.useContext(AuthContext);
  const canSeeAccessGroups = user.isAllowed(to.seeWorkerProfileAccessGroups);
  const [selectedSiteId, setSelectedSiteId] = React.useState<string | null>(
    null,
  );
  const accessGroupSitesQuery = useGetWorkerAccessGroupSitesQuery({
    variables: { workerId: worker.workerId },
    skip: !canSeeAccessGroups,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const sites = [
    ...new Map([
      ...jobsiteWorkers.map(
        (assignment): [string, { value: string; label: string }] => {
          const site = assignment.jobsiteContractor.jobsite;
          return [site.jobsiteId, { value: site.jobsiteId, label: site.name }];
        },
      ),
      ...(accessGroupSitesQuery.data?.workerAccessGroupSites ?? []).map(
        (site): [string, { value: string; label: string }] => [
          site.siteId,
          { value: site.siteId, label: site.name },
        ],
      ),
    ]).values(),
  ];
  const siteId =
    sites.find((site) => site.value === selectedSiteId)?.value ??
    sites[0]?.value;
  const onRefreshChange = React.useCallback(
    (refresh: () => Promise<unknown>) =>
      onTabApiChange?.({ refetchData: refresh }),
    [onTabApiChange],
  );
  if (!canSeeAccessGroups)
    return (
      <p className="odin-p-6">
        You do not have permission to view access groups.
      </p>
    );
  if (accessGroupSitesQuery.error)
    return (
      <AccessGroupsError
        retry={() => {
          void accessGroupSitesQuery.refetch().catch((): void => undefined);
        }}
      />
    );
  if (accessGroupSitesQuery.loading) return <LoadingError loading />;
  if (!siteId)
    return (
      <p className="odin-p-6">
        This worker has no site assignments or membership history. Assign a site
        before managing memberships.
      </p>
    );
  const assignments = jobsiteWorkers.filter(
    (assignment) => assignment.jobsiteContractor.jobsite.jobsiteId === siteId,
  );
  const eligibility = assignments.some(
    (assignment) => assignment.currentAccess?.isAllowed,
  )
    ? 'Current site status: allowed.'
    : 'Current site status: not allowed. A membership does not override suspension, offboarding or other site access requirements.';
  return (
    <div className="odin-p-6 odin-space-y-6">
      <NewHeader
        title="Access groups"
        size="md"
        actionsProps={{ headerActions: [] }}
      />
      <Select
        name="membership-site"
        instanceId="membership-site"
        inputId="membership-site"
        label="Site"
        options={sites}
        value={sites.find((site) => site.value === siteId)}
        onChange={(option) => setSelectedSiteId(option?.value ?? null)}
      />
      {/* Keyed by site, so one site's flags never decide another site's groups. */}
      <JobsiteFeatureFlagsProvider key={siteId} jobsiteId={siteId}>
        <WorkerSiteAccessGroupsGrantingByFlag
          key={`${worker.workerId}:${siteId}`}
          siteId={siteId}
          workerId={worker.workerId}
          eligibility={eligibility}
          hasSiteAssignment={assignments.length > 0}
          onRefreshChange={onRefreshChange}
        />
      </JobsiteFeatureFlagsProvider>
    </div>
  );
}
